import { ProductCatalogItem } from "@/app/services/configService/getProductsCatalog";
import { DocumentTransaction } from "@/app/services/supportService/createSupport";
import { cn } from "@/app/utils/cn";
import { formatName } from "@/app/utils/formatName";
import { PartnerType } from "@/app/utils/partners.enum";
import { PartnerImage } from "@/view/components/PartnerImage";
import { Spinner } from "@/view/components/ui/spinner";

import { formatDaysAgo } from "../formatDaysAgo";

interface PendingDocumentsProps {
  data: DocumentTransaction[];
  isLoading?: boolean;
  onSelect: (item: DocumentTransaction) => void;
  selectedId?: string
  showQueueHint?: boolean;
  productsCatalog?: ProductCatalogItem[];
}

export const PendingDocuments = ({
  data,
  isLoading,
  onSelect,
  selectedId,
  showQueueHint,
  productsCatalog
}: PendingDocumentsProps) => (
  <div className="flex flex-1 flex-col gap-4">
    {showQueueHint && (
      <div className="rounded-xl border border-dashed border-gray01 bg-gray-50 p-3 text-center text-sm text-black04">
        Selecione um chamado na fila para carregar o contexto automaticamente ·
        ou busque direto na barra acima
      </div>
    )}

    <div className="flex flex-1 flex-col gap-3 rounded-xl border border-gray01 bg-gray-50 p-4">
      <div>
        <p className="text-sm font-semibold uppercase tracking-wide text-gray-500">
          Documentos pendentes
        </p>
        <p className="text-xs text-black04">
          Propostas aguardando anexo — independente de chamado
        </p>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-10">
          <Spinner />
        </div>
      ) : data.length === 0 ? (
        <p className="py-6 text-center text-sm text-black04">
          Nenhuma proposta aguardando anexo
        </p>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 2xl:grid-cols-3">
          {data.map((item) => (
            <PendingCard
              selected={selectedId === item._id}
              key={item._id}
              item={item}
              productsCatalog={productsCatalog}
              onClick={() => onSelect(item)}
            />
          ))}
        </div>
      )}
    </div>
  </div>
);

interface PendingCardProps {
  item: DocumentTransaction;
  onClick: () => void;
  selected?: boolean;
  productsCatalog?: ProductCatalogItem[];
}

const PendingCard = ({
  item,
  onClick,
  selected,
  productsCatalog,
}: PendingCardProps) => (
  <button
    type="button"
    onClick={onClick}
    // items-stretch vence o align-items do <button> no UA stylesheet, senão as
    // linhas crescem com o conteúdo e o contrato longo vaza do card
    className={cn(
      "flex min-w-0 flex-col items-stretch gap-2 rounded-xl border border-gray01 bg-white p-4 text-left transition-colors hover:bg-white02",
      selected && "border-primary",
    )}
  >
    <div className="flex items-start justify-between gap-2">
      {/* min-w-0 pro contrato cortar em "..." em vez de empurrar a badge */}
      <span className="min-w-0 truncate text-lg font-semibold text-black03">
        {item.contract || "-"}
      </span>
      {!!item.type && (
        <div className="shrink-0">
          <PartnerImage
            type={item.type as PartnerType}
            products={productsCatalog}
          />
        </div>
      )}
    </div>

    <p className="truncate text-sm font-medium text-gray-600">
      {formatName(item.customerName) || "Cliente não identificado"}
    </p>

    <div className="flex items-center justify-between gap-2 border-t border-gray01 pt-2">
      {!!item.documentsCount && (
        <span
          className={cn(
            "whitespace-nowrap rounded-full bg-gray02 px-3 py-1 text-xs font-semibold text-gray-600",
            !!selected && "bg-primary text-white",
          )}
        >
          {item.documentsCount}{" "}
          {item.documentsCount === 1 ? "pendente" : "pendentes"}
        </span>
      )}

      <span className="ml-auto text-xs text-black04 font-medium">
        {formatDaysAgo(item.updatedAt)}
      </span>
    </div>
  </button>
);
